<template>
  <div>
    <ontos-instance-reverse-rel-input
        v-if="property.relationship && property.relationship.reverse"
        ref="reverseInput"
        :property="property"
        :value="shownValue"
        :loading="pending"
        :items="offeredDomain"
        :reverse-info="reverseInfo"
        :idea-alias="ideaAlias"
        :instance-id="instanceId"
        :system-alias="systemAlias"
        :readonly="readonly || !reverseInfo"
        :error-message="errorMessage"
        :hint="guidance"
        @input="$emit('input', $event)"
        @change="$emit('change', $event)"
        @showMessage="$emit('showMessage', $event)"/>
    <number-input
        v-else-if="!property.multiple && property.type === 'number' && !property.formula"
        style="flex-basis: auto"
        :class="applyClass"
        :type="property.subtype"
        :style="numberWidth(property)"
        :label="property.name"
        :value="$utils.isNullOrUndefined(value)? undefined : Number(value)"
        :readonly="readonly"
        @input="
              $emit('input', Number($event))
            "
        @blur="
              $emit('change', value)
            "
        :counter="property.length"
        @keydown="$emit('keydown', $event)"
        :error-message="errorMessage"
    />
    <ontos-media-field
        v-else-if="isMediaType(property)"
        :type="property.type"
        :value="value"
        :label="property.name"
        :readonly="readonly"
        :class="applyClass"
        :error-message="errorMessage"
        @input="$emit('input', $event)"
        @change="$emit('change', $event)"/>
    <!-- applyClass aqui também: é ele que carrega `required`, e o `*` do rótulo vem de
         uma regra `.required label::after`. Sem a classe no contêiner, o campo markdown
         era o único que não marcava obrigatório. -->
    <div v-else-if="property.type === 'text' && property.subtype === 'markdown'" :class="applyClass">
      <label class="ontos-field-label">{{ property.name }}</label>
      <!-- Somente leitura é o Viewer, não o Editor: no Toast UI v3 o editor não tem modo
           bloqueado, e era por isso que propriedade calculada ou marcada como readonly
           continuava editável aqui — o único campo do formulário que não respeitava a
           trava. É o mesmo Viewer que o PropertyValue usa para exibir markdown. -->
      <markdown-viewer v-if="readonly" :initialValue="shownMarkdown(value, workspace, item || undefined, ideaAlias)"/>
      <!-- Sem initialValue: o conteúdo entra depois de montar, e sem mexer no cursor.
           Com initialValue o editor tenta posicionar o cursor no começo do documento já
           no construtor, e quando o primeiro nó é uma lista essa posição não aceita
           seleção de texto — o ProseMirror por baixo lança. -->
      <!-- `mentions`: as referências do app — itens, coleções, pessoas e campos —, com o `@` e o botão
           da barra (pedido do usuário). O item dá os nomes de agora (`_mentions`) -->
      <markdown-editor v-else initialEditType="wysiwyg" mentions :item="item" :idea-alias="ideaAlias"
                       @change="onChangeMarkdown" @showMessage="$emit('showMessage', $event)" ref="markdown-editor"
      />
      <v-messages v-if="errorMessage" active :messages="errorMessage" color="error" class="mt-1" role="alert"/>
    </div>

    <!-- A calculada: somente leitura, e com o valor LEGÍVEL — o nome da opção, a data no formato
         de quem lê. O valor vem do servidor na forma gravada, e mostrá-lo cru exibiria o id de uma
         seleção calculada. A seleção, o relacionamento e o sim/não calculados não passam aqui —
         ver `computedAsText`. O `applyClass` traz o 🔒 do rótulo, como nos outros campos somente
         leitura — sem ele, a calculada parecia editável (relato do usuário). -->
    <v-text-field
        v-else-if="computedAsText"
        :class="applyClass"
        readonly
        :model-value="pending ? '' : computedText"
        :loading="pending"
        :label="property.name"
        @keydown="$emit('keydown', $event)"
    />
    <!-- Antes do multiple-field: seleção múltipla é escolha entre opções fixas, e não a
         lista livre de valores que o multiple-field edita. -->
    <ontos-choice-input v-else-if="property.type === 'choice'"
                        :label="property.name"
                        :value="shownValue"
                        :options="offeredOptions"
                        :multiple="!!property.multiple"
                        :readonly="readonly"
                        :loading="pending" :disabled="pending"
                        :required="required"
                        :class="applyClass"
                        :error-message="errorMessage"
                        :hint="guidance"
                        @input="$emit('change', $event)"/>
    <!-- Usuário: as pessoas do ambiente, com a cara, o nome e o e-mail, achadas por um ou outro.
         Escolher é uma escolha inteira, como na seleção: avisa a mudança na hora. -->
    <ontos-person-select v-else-if="property.type === 'user'"
                         :people="offeredPeople"
                         :label="property.name"
                         :model-value="shownValue"
                         :multiple="!!property.multiple"
                         :readonly="readonly"
                         :loading="pending" :disabled="pending"
                         :clearable="clearable"
                         :menu-icon="menuIconOf(readonly)"
                         :class="applyClass"
                         :error-messages="errorMessage"
                         :hint="guidance" :persistent-hint="!!guidance"
                         @update:model-value="$emit('change', $event)"/>
    <ontos-creatable-autocomplete v-else-if=isOwnedRelationship(property)
                    :label="property.name"
                    :items="offeredDomain.map(domainInstance => domainInstance.name? domainInstance :
                          $copy.shallowCopy(domainInstance, {name: `${property.relationship.idea} #${domainInstance._instance}`}))"
                    item-title="_identifier"
                    :class="applyClass"
                    item-value="_instance"
                    :readonly="readonly"
                    :loading="pending" :disabled="pending"
                    :menu-icon="menuIconOf(readonly)"
                    :multiple="property.multiple"
                    auto-select-first
                    :error-messages="errorMessage"
                    :hint="guidance" :persistent-hint="!!guidance"
                    :model-value="relValue(property)"
                    @update:model-value="$emit('input', keepFixed($event))"
                    @blur="$emit('change', value)"
                    @create="openCreateRel({property, name: $event})">
      <!-- A régua na opção e no escolhido: o desenho de todo campo de relacionamento -->
      <template #item="{item, props}">
        <ontos-rel-option v-bind="props" :item="item"/>
      </template>
      <template #selection="{item}">
        <ontos-rel-selection :item="item" :removable="property.multiple ? !readonly && !isFixed(item) : clearable"
                             @remove="remove($event)"/>
      </template>
    </ontos-creatable-autocomplete>
    <multiple-field v-else-if="property.multiple"
                    :value="value"
                    :type="property.type"
                    :class="applyClass"
                    :subtype="property.subtype"
                    :error-message="errorMessage"
                    @input="
              $emit('input', $event)
            "
                    @blur="
              $emit('change', value)
            "
                    :label="property.name">
    </multiple-field>

    <!-- applyClass no contêiner, como no markdown: é ele que carrega `required`, e o `*` do
         rótulo vem de `.required label::after`. -->
    <ontos-boolean-input v-else-if="property.type === 'boolean'"
                         :class="applyClass"
                         :label="property.name"
                         :value="shownValue"
                         :required="required"
                         :readonly="readonly"
                         :loading="pending"
                         :error-message="errorMessage"
                         @change="$emit('change', $event)"/>
    <v-text-field
        v-else
        style="flex-basis: auto"
        :type="property.type"
        :style="numberWidth(property)"
        :label="property.name"
        :model-value="value"
        :readonly="readonly"
        :class="applyClass"
        :placeholder="property.type === 'date'? new Date().toISOString() : undefined"
        @update:model-value="onTyped($event)"
        @blur="onLeft()"
        :counter="property.length"
        @keydown="$emit('keydown', $event)"
        :error-messages="errorMessage"
    />
    <!-- O que o item já violava neste campo, e ninguém mexeu ainda: aviso, e não erro — a catraca
         deixa salvar. No espaço da mensagem de erro do campo, quando ele o reserva -->
    <ontos-field-failures :failures="warnings" :class="{'warnings-in-details': reservesDetails}"/>
    <create-instance-dialog v-if="createRel && createRelContext"
                            ref="createRel"
                            :context="createRelContext"
                            :initialName="createRelName"
                            :full-page="false"
                            @showMessage="$emit('showMessage', $event)"
                            @saved="onSavedRel"
                            @closed="closeRel"/>
  </div>
</template>
<script lang="ts">
import {Component, Vue, Prop, Inject, toNative} from 'vue-facing-decorator'
import MultipleField from "@/components/shared/MultipleField.vue"
import NumberInput from "@/components/shared/NumberInput.vue"
import CreateInstanceDialog from "@/components/live/instance/CreateInstanceDialog.vue"
import InstanceReverseRelInput from "@/components/live/instance/InstanceReverseRelInput.vue"
import MediaField from "@/components/shared/MediaField.vue"
import ChoiceInput from "@/components/live/instance/ChoiceInput.vue"
import BooleanInput from "@/components/live/instance/BooleanInput.vue"
import FieldFailures from "@/components/live/instance/FieldFailures.vue"
import {FieldFailure} from "@/utils/fieldValidation"
import CreatableAutocomplete from "@/components/shared/CreatableAutocomplete.vue"
import {
  displayKind, formatSimple, isChoiceField, isEmpty, optionIdOf, relatedId, ValueKind
} from "@/utils/propertyDisplay"
import PersonSelect from "@/components/shared/PersonSelect.vue"
import {WORKSPACE, WORKSPACE_PEOPLE} from "@/components/shared/WorkspaceRouteComponent"
import {EnhancedWorkpace} from "@/types/Workspace"
import {shownMarkdown} from "@/utils/markdownMentions"
import {WorkspacePerson} from "@/types/Person"
import RelOption from "@/components/live/instance/RelOption.vue"
import RelSelection from "@/components/live/instance/RelSelection.vue"
import {DomainOption} from "@/types/Domain"
import {isMediaType} from "@/utils/mediaTypes"
import {isClearable, menuIconOf} from "@/utils/fieldRules"
import MarkdownEditor from '@/components/shared/markdown/MarkdownEditor.vue'
import MarkdownViewer from '@/components/shared/markdown/MarkdownViewer.vue'

/** Os campos que desenham a própria mensagem embaixo, sem o espaço que o Vuetify reserva. */
const OWN_MESSAGE: ValueKind[] = ['boolean', 'markdown', 'image', 'audio']

@Component({
  emits: ['input', 'change', 'showMessage', 'keydown'],
  components: {
    'ontos-rel-option': RelOption,
    'ontos-rel-selection': RelSelection,
    NumberInput,
    MultipleField,
    MarkdownEditor,
    'markdown-viewer': MarkdownViewer,
    CreateInstanceDialog,
    'ontos-instance-reverse-rel-input': InstanceReverseRelInput,
    'ontos-media-field': MediaField,
    'ontos-choice-input': ChoiceInput,
    'ontos-boolean-input': BooleanInput,
    'ontos-field-failures': FieldFailures,
    'ontos-creatable-autocomplete': CreatableAutocomplete,
    'ontos-person-select': PersonSelect
  }
})
class InstancePropertyInput extends Vue {
  //expostos ao template: são funções de módulo
  isMediaType = isMediaType
  menuIconOf = menuIconOf

  /**
   * Tira um item da escolha — da lista, ou o único.
   *
   * Existe porque o chip saiu: era dele que vinha o X, com a remoção que o Vuetify fazia
   * sozinho. O que sai daqui é o mesmo valor que o campo emitiria — só o desenho mudou. No de um
   * valor só, o × junto do valor fica no lugar do X de limpar do Vuetify, que ficava longe dele e só
   * aparecia com o mouse por cima (relato do usuário: o modelo é o da Parceria).
   */
  remove(item: any) {
    const id = optionIdOf(item)
    const kept = this.property.multiple
        ? this.$arrays.asArray(this.value).filter((current: any) => Number(current) !== id)
        : null
    this.$emit('input', kept)
    //O × é um gesto acabado: avisa a mudança já (a prévia), sem esperar sair do campo — o clique nele
    //nem sempre passa pelo foco, e o `blur` que avisaria podia não vir
    this.$emit('change', kept)
  }

  /** Este item é um dos que não podem sair (`fixed`)? Ele fica sem o X de remover. */
  isFixed(item: any): boolean {
    const id = optionIdOf(item)
    return id !== undefined && this.fixed.includes(id)
  }

  /**
   * A escolha nova, com os fixos de volta se tiverem saído.
   *
   * Sem o X o fixo não sai pelo botão, mas sai por outros caminhos do campo: desmarcá-lo na lista,
   * apagar com a tecla, limpar o campo inteiro. Repor aqui fecha todos de uma vez, em vez de travar
   * cada caminho do Vuetify.
   */
  keepFixed(value: any): any {
    if (!this.fixed.length) return value
    const ids = this.$arrays.asArray(value).map((id: any) => Number(id))
    return [...this.fixed.filter(id => !ids.includes(id)), ...ids]
  }

  /** Enquanto a carga inicial do markdown acontece, o `change` dela não conta como edição */
  private fillingMarkdown = false

  @Prop({type: Object, required: true}) property!: any
  @Prop({required: true}) value!: any
  @Prop({type: Array}) domain!: any[]
  /** As opções do domínio, quando o atributo é de seleção. Não confundir com o `domain`
   *  acima, que é a lista de itens candidatos de um relacionamento. */
  @Prop({type: Array, default: () => []}) options!: DomainOption[]
  /**
   * Num campo com "Opções permitidas", os ids que o filtro deixa escolher — o servidor julga, pela
   * prévia. Sem ele (campo sem filtro, ou a prévia ainda não respondeu), tudo é oferecido.
   */
  @Prop({type: Array, default: undefined}) readonly allowed?: number[]
  @Prop({type: Boolean, default: false}) readonly!: boolean
  /**
   * Num relacionamento múltiplo, os ids que não podem sair da escolha — o dono de quem se cria pelo
   * "Adicionar" da ficha dele. Os outros continuam livres. Ver o `fixedValues` do InstanceCreate.
   */
  @Prop({type: Array, default: () => []}) readonly fixed!: number[]
  @Prop({type: Boolean, default: false}) required!: boolean
  @Prop({type: Boolean, default: false}) unique!: boolean
  @Prop({type: String}) errorMessage!: string
  /** O que o item já violava neste campo, enquanto ninguém o mexe — ver `InstanceInput.warningsOf`. */
  @Prop({type: Array, default: () => []}) readonly warnings!: FieldFailure[]
  @Prop({type: String}) systemAlias!: string
  //metadados da propriedade reversa, vindos do servidor
  @Prop() reverseInfo!: any
  @Prop({type: String, default: ""}) ideaAlias!: string
  @Prop({default: null}) instanceId!: number | null
  /**
   * A prévia em curso vai mudar este campo — o valor da calculada, ou as opções permitidas (ver o
   * `InstanceInput.affectedBy`): até ela responder, o campo fica com o progresso do próprio Vuetify
   * (`loading`), travado e sem o valor velho (pedido do usuário). Só os campos que a prévia muda o recebem.
   */
  @Prop({type: Boolean, default: false}) readonly pending!: boolean
  /** As pessoas do ambiente, para o campo de usuário — ver `WORKSPACE_PEOPLE`. */
  @Inject({from: WORKSPACE_PEOPLE, default: () => []}) readonly people!: WorkspacePerson[]
  /** O ambiente, para as referências do markdown — ver `markdownMentions`. */
  @Inject({from: WORKSPACE, default: null}) readonly workspace!: EnhancedWorkpace | null
  /**
   * O item inteiro, dono do valor: no markdown, os nomes de agora das referências (`_mentions`) e os
   * valores do `{{campo}}`. Ausente fora do formulário do item.
   */
  @Prop({type: Object, default: null}) readonly item!: Record<string, any> | null

  //Exposta ao template: função de módulo não é enxergada de lá
  shownMarkdown = shownMarkdown

  createRel = false
  createRelName = ''
  createRelContext: any = null
  createRelProperty: any = null

  /**
   * O que este campo oferece: com "Opções permitidas", só o que o filtro deixa (`allowed`) — em vez
   * de deixar escolher o que o salvar recusaria. E sempre o que JÁ está escolhido, mesmo que não passe
   * mais: o item não perde o dado em silêncio, e o salvar não cobra o que já estava escolhido.
   *
   * Um caminho para os três: as opções da seleção, os candidatos do relacionamento e os da lista
   * reversa.
   */
  offered<T>(candidates: T[], idOf: (candidate: T) => number | undefined): T[] {
    if (!this.allowed) return candidates
    const allowed = this.allowed
    const chosen = this.$arrays.asArray(this.value).map((value: any) => relatedId(value))
    return candidates.filter(candidate => {
      const id = idOf(candidate)
      return id !== undefined && (allowed.includes(id) || chosen.includes(id))
    })
  }

  get offeredOptions(): DomainOption[] {
    return this.offered(this.options, option => option.id)
  }

  get offeredDomain(): any[] {
    return this.offered(this.domain || [], item => relatedId(item))
  }

  /**
   * As pessoas que o campo de usuário oferece: as que se podem escolher (membro admitido) — e quem
   * já está escolhido, mesmo que tenha saído do ambiente, para o valor continuar aparecendo com
   * nome (decisão do usuário). O filtro das "Opções permitidas", pelo mesmo `offered`.
   */
  get offeredPeople(): WorkspacePerson[] {
    const chosen = this.$arrays.asArray(this.value).map((value: any) => relatedId(value))
    return this.offered(this.people.filter(person => person.assignable || chosen.includes(person.user)),
        person => person.user)
  }

  /** A regra do X é a mesma de todo campo de escolha, e mora no isClearable. */
  get clearable(): boolean {
    return isClearable({
      multiple: this.property.multiple, readonly: this.readonly, required: this.required
    })
  }

  /**
   * A calculada que aparece como texto somente leitura. As que têm campo próprio — seleção,
   * relacionamento, usuário e sim/não — usam o dele, travado, para ter o mesmo desenho das gravadas:
   * numa caixa de texto perdiam a cor da opção, a régua do item, a cara da pessoa e o ✓ Sim / ✗ Não.
   */
  get computedAsText(): boolean {
    return !!this.property.formula && !isChoiceField(this.property) && this.property.type !== 'boolean'
  }

  /**
   * A orientação das "Opções permitidas" (pedido do usuário): o que o designer escreveu sobre o que a
   * lista oferece. Vai no `hint` do próprio campo do Vuetify — o lugar das mensagens dele, que o erro
   * cobre sozinho —, e não num componente à parte, que caía fora desse espaço. Só quando há o que
   * escolher (o campo travado não oferece nada), e não por baixo do aviso ⚠, que usa o mesmo lugar.
   */
  get guidance(): string {
    return (!this.readonly && !this.warnings.length && this.property.optionFilterMessage) || ''
  }

  /**
   * O campo reserva, embaixo, o espaço da mensagem de erro? Os do Vuetify sim, e o aviso sobe para
   * dentro dele (pedido do usuário: no lugar do erro). Os que desenham a própria mensagem — o sim/não,
   * o markdown, a mídia — não reservam, e o aviso fica logo abaixo. A lista reversa reserva desde que
   * a dica dela passou para o `hint` do campo.
   */
  get reservesDetails(): boolean {
    return !OWN_MESSAGE.includes(displayKind(this.property))
  }

  /**
   * O campo avisa a mudança (`change`) a cada valor, e não ao sair dele? A data sim, como a seleção
   * e a caixa: o `<input type="date">` só entrega valor quando a data está completa — ou vazio —,
   * então cada valor já é uma escolha. Ao sair, como era, a prévia não ouvia quem escolhia no
   * calendário e ficava no campo: a regra da Data final seguia acusando depois de corrigida a
   * inicial (relato do usuário).
   */
  get changesAtOnce(): boolean {
    return this.property.type === 'date'
  }

  onTyped(value: any) {
    this.$emit('input', value)
    if (this.changesAtOnce) this.$emit('change', value)
  }

  /** Saiu do campo: é aqui que o texto avisa a mudança — a data já avisou a cada valor. */
  onLeft() {
    if (!this.changesAtOnce) this.$emit('change', this.value)
  }

  /** O valor de uma calculada como se lê, pelo `formatSimple` — o mesmo que formata a data na ficha. */
  get computedText(): string {
    return this.$arrays.asArray(this.value)
        .filter((value: any) => !isEmpty(value))
        .map(value => formatSimple(this.property, value))
        .join(', ')
  }

  get applyClass() {
    return {
      required: this.required,
      readonly: this.readonly,
      unique: this.unique
    }
  }

  /**
   * Largura do campo de número, sob medida para o conteúdo: o que for maior entre o
   * rótulo e o valor, com duas casas de folga.
   *
   * A medida é em `em`, como sempre foi, e vale porque os campos do app são
   * `underlined` — essa variante zera o recuo horizontal do campo
   * (`--v-field-padding-start`/`-end`), então a largura toda é do texto. Se algum dia o
   * app passar a `filled` ou `outlined`, esses 16px de cada lado precisam entrar na
   * conta: o `box-sizing` é `border-box` e eles saem de dentro.
   *
   * O `size` do <input>, que acompanhava esta largura, saiu daqui e dos outros três
   * campos: o `.v-field__input` do Vuetify 4 é `width: 100%`, então o atributo não
   * decide mais largura nenhuma.
   */
  numberWidth(property: any): string | null {
    if (!property || property.type !== 'number') return null
    const label = property.name ? property.name.length + 2 : 0
    const content = this.$utils.isNullOrUndefined(this.value) ? 0 : String(this.value).length
    return `width: ${Math.max(2, label, content) - 1}em`
  }

  /** O valor à vista: nenhum enquanto a prévia não responde (`pending`) — o velho enganaria. */
  get shownValue(): any {
    return this.pending ? null : this.value
  }

  /**
   * Os candidatos correspondentes ao que está escolhido.
   *
   * **Id que não acha candidato sai da lista.** Um buraco ali — o `null` que sobra de uma
   * posição apagada, um candidato que foi excluído — não é valor escolhido. Mapeado sem filtro
   * ele entrava no modelo como `undefined`, e o Vuetify chama o slot de seleção com o valor
   * CRU: ler o identificador de um nada derrubava o campo inteiro, que sumia da tela sem dizer
   * por quê. Foi assim que uma canção com `parceria: [1, null]` ficou sem o campo Parceria.
   *
   * Filtrando, o campo mostra o que de fato existe — e salvar limpa o buraco, porque o que vai
   * ao servidor é o que está no campo.
   *
   * O relacionamento calculado chega com os itens já resumidos (id e identificador, do `_rels` ou
   * da prévia), e não com ids: valem eles mesmos, sem depender de estarem entre os candidatos.
   */
  relValue(property: any) {
    if (this.pending) return undefined
    const instances = this.$arrays.objectOf(this.domain, '_instance', true)
    const itemOf = (value: any) => property.formula ? value : instances[value]
    if (property.multiple) {
      const values = this.$arrays.asArray(this.value)
          .map(itemOf)
          .filter((value: any) => !!value)
      return values.length ? values : undefined
    }
    return isEmpty(this.value) ? undefined : itemOf(this.value)
  }

  /**
   * O InstanceInput guarda ref deste componente, não do input reverso que é filho
   * dele. Sem esta ponte a coleta de pendências não acha nada.
   */
  pendingItems() {
    const input = this.$refs.reverseInput as any
    return input && input.pendingItems ? input.pendingItems() : []
  }

  isOwnedRelationship(property: any) {
    //relationship é objeto {idea, reverse?}: a comparação com a string 'reverse'
    //nunca dava true, então esta guarda não existia na prática
    if (property.relationship && property.relationship.reverse) return false
    return !!property.relationship
  }

  openCreateRel({property, name}: {property: any; name: string}) {
    this.createRel = true
    this.createRelName = name
    this.createRelContext = {system: this.systemAlias, idea: property.relationship.idea}
    this.createRelProperty = property
  }

  async onSavedRel(relInstance: any) {
    const prop = this.createRelProperty
    this.domain.push(relInstance)
    if (prop.multiple) {
      const elements = this.value ? [...this.value] : []
      elements.push(relInstance._instance)
      this.$emit('change', elements)
    } else this.$emit('change', relInstance._instance)
    this.closeRel()
  }

  closeRel() {
    this.createRel = false
    this.createRelContext = null
    this.createRelName = ''
    this.createRelProperty = null
  }

  /**
   * Carrega o markdown depois de montado, e sem levar o cursor junto.
   *
   * `cursorToEnd` em false é o ponto: é o reposicionamento do cursor que estoura quando
   * o documento começa com uma lista. Uma vez só, no mount, como o initialValue fazia —
   * reagir à prop brigaria com o que está sendo digitado.
   */
  mounted() {
    const editor = this.$refs['markdown-editor'] as InstanceType<typeof MarkdownEditor> | undefined
    if (!editor || this.$utils.isNullOrUndefined(this.value)) return
    //A carga inicial não é edição do usuário: sem a trava, o `change` que ela dispara
    //emitiria input e o formulário nasceria sujo — Salvar ativo e aviso ao fechar.
    this.fillingMarkdown = true
    editor.setMarkdown(this.value, false)
    this.fillingMarkdown = false
  }

  onChangeMarkdown() {
    if (this.fillingMarkdown) return
    const content = (this.$refs['markdown-editor'] as InstanceType<typeof MarkdownEditor>).getMarkdown()
    this.$emit('input', content)
  }
}

export default toNative(InstancePropertyInput)
</script>

<style scoped>
/* O aviso no espaço da mensagem de erro: o campo do Vuetify reserva 22px embaixo
   (`.v-input__details`), vazios quando não há erro — e só aparece aviso quando não há. */
.warnings-in-details {
  margin-top: -22px;
}

</style>
